import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles } from "@lib/sharedStyles";
import { QuestionType, QuestionTypeGerman } from "../germanMapping";

// Register components (side-effect imports to ensure custom elements are defined)
import "@/components/button/BasicButton";
import "@/components/inputs-textarea/BasicInput";
import "@/components/select/BasicSelect";

interface Option { id: string; name: string }
interface QuestionRow {
  id: string;
  question: string;
  answer: string;
  category: Option;
  difficulty: number;
  question_type?: QuestionType | null;
  dirty?: boolean;
  saving?: boolean;
  error?: string | null;
}

// Determine backend API base. When running via Vite dev server on port 5173,
// route API calls to Django on port 8002; otherwise use same-origin.
const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';

function getCSRFToken(): string | null {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') return decodeURIComponent(value);
  }
  return null;
}

function navigate(path: string) {
  const state = {};
  window.history.pushState(state, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

@customElement('questions-page')
export default class QuestionsPage extends LitElement {
  static readonly styles = [
    sharedStyles,
    css`
	    :host { display: block; padding: 1rem; }
	    table { width: 100%; border-collapse: collapse; }
	    th, td { border: 1px solid #ddd; padding: 0.5rem; text-align: left; }
	    th { background: #f7f7f7; }
	    select, input[type="text"] { width: 100%; box-sizing: border-box; padding: 0.35rem; }
	    .actions { white-space: nowrap; }
	    .error { color: #b00020; font-size: 0.9rem; }
	    .saving { opacity: 0.6; }
	    .toolbar { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.75rem; }
	    .toolbar h2 { margin: 0; font-size: 1.25rem; }
	    .badge { display: inline-block; padding: 0.1rem 0.4rem; border-radius: 0.4rem; background: #eef; color: #225; font-size: 0.8rem; }
	  `
  ];

  @state() private loading = true;
  @state() private error: string | null = null;
  @state() private questions: QuestionRow[] = [];
  @state() private categories: Option[] = [];
  @state() private difficulties: number[] = [];

  // Filters
  @state() private filterCategoryId: string = '';
  @state() private filterDifficultyId: number | '' = '';

  @state() private newQ = { question: '', answer: '', category_id: '', difficulty_id: '', question_type: '' } as any;
  @state() private creating = false;
  @state() private createError: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadAll();
  }

  private async loadAll() {
    this.loading = true;
    this.error = null;
    try {
      const [qs, cats, diffs] = await Promise.all([
        fetch(`${API_BASE}/api/jeopardy/questions`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`Failed to load questions (${r.status})`); return r.json(); }),
        fetch(`${API_BASE}/api/jeopardy/categories`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`Failed to load categories (${r.status})`); return r.json(); }),
        fetch(`${API_BASE}/api/jeopardy/difficulties`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`Failed to load difficulties (${r.status})`); return r.json(); }),
      ]);
      this.categories = cats.results ?? [];
      this.difficulties = diffs.results ?? [];
      this.questions = (qs.results ?? []).map((q: any) => ({ ...q, dirty: false, saving: false, error: null }));
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to load data';
    } finally {
      this.loading = false;
    }
  }

  private onEdit(idx: number, field: 'question' | 'answer', value: string) {
    const copy = [...this.questions];
    copy[idx] = { ...copy[idx], [field]: value, dirty: true };
    this.questions = copy;
  }

  private onSelect(idx: number, field: 'category' | 'difficulty', value: string) {
    const copy = [...this.questions];
    if (field === 'category') {
      const selected = this.categories.find(c => c.id === value);
      if (!selected) return;
      copy[idx] = { ...copy[idx], category: selected, dirty: true };
    } else {
      copy[idx] = { ...copy[idx], difficulty: Number(value), dirty: true };
    }
    this.questions = copy;
  }

  private async saveRow(idx: number) {
    const row = this.questions[idx];
    if (!row?.id) return;

    const payload: any = {
      question: row.question,
      answer: row.answer,
      category_id: row.category?.id,
      difficulty_id: row.difficulty,
    };

    this.setSaving(idx, true, null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const csrf = getCSRFToken();
      if (csrf) headers['X-CSRFToken'] = csrf;
      const res = await fetch(`${API_BASE}/api/jeopardy/questions/${encodeURIComponent(row.id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const updated = await res.json();
      const copy = [...this.questions];
      copy[idx] = { ...updated, dirty: false, saving: false, error: null };
      this.questions = copy;
    } catch (e: any) {
      this.setSaving(idx, false, e?.message ?? 'Save failed');
    }
  }

  private setSaving(idx: number, saving: boolean, error: string | null) {
    const copy = [...this.questions];
    copy[idx] = { ...copy[idx], saving, error };
    this.questions = copy;
  }

  private renderRow(q: QuestionRow, idx: number) {
    // Prepare options for basic-select
    const categoryOptions = this.categories.map(c => ({ value: c.id, label: c.name }));
    const difficultyOptions = this.difficulties.map(d => ({ value: String(d), label: String(d) }));

    return html`
      <tr class=${q.saving ? 'saving' : ''}>
        <td>
          <basic-input
            .value=${q.question}
            .label=${''}
            .name=${'question'}
            .id=${`q-${idx}-question`}
            .inputEventIdentifier=${String(idx)}
            @input-change=${(e: any) => this.onRowInputChange(e)}
          ></basic-input>
        </td>
        <td>
          <basic-input
            .value=${q.answer}
            .label=${''}
            .name=${'answer'}
            .id=${`q-${idx}-answer`}
            .inputEventIdentifier=${String(idx)}
            @input-change=${(e: any) => this.onRowInputChange(e)}
          ></basic-input>
        </td>
        <td>
          <basic-select
            .value=${q.category?.id ?? ''}
            .options=${categoryOptions}
            .rowIndex=${String(idx)}
            .additionalCustomEventIdentifier=${'category'}
            @selection-change=${(e: any) => this.onRowSelectionChange(e)}
          ></basic-select>
        </td>
        <td>
          <basic-select
            .value=${q.difficulty != null ? String(q.difficulty) : ''}
            .options=${difficultyOptions}
            .rowIndex=${String(idx)}
            .additionalCustomEventIdentifier=${'difficulty'}
            @selection-change=${(e: any) => this.onRowSelectionChange(e)}
          ></basic-select>
        </td>
        <td>${QuestionTypeGerman[q.question_type ?? ''] ?? q.question_type ?? '-'}</td>
        <td class="actions">
          <basic-button ?disabled=${!q.dirty || q.saving} @click=${() => this.saveRow(idx)}>Save</basic-button>
          ${q.error ? html`<div class="error">${q.error}</div>` : nothing}
        </td>
      </tr>
    `;
  }

  private onRowInputChange(e: any) {
    const idx = Number(e.detail.additionalIdentifier);
    const name = e.detail.name as 'question' | 'answer';
    const value = e.detail.value;
    if (!Number.isNaN(idx)) {
      this.onEdit(idx, name, value);
    }
  }

  private onRowSelectionChange(e: any) {
    const idx = Number(e.detail.rowIndex);
    const field = e.detail.additionalCustomEventIdentifier as 'category' | 'difficulty';
    const value = e.detail.newValue;
    if (!Number.isNaN(idx)) {
      this.onSelect(idx, field, value);
    }
  }

  private onNewInputChange(e: any) {
    const name = e.detail.name as string;
    const value = e.detail.value;
    if (name === 'question') this.newQ = { ...this.newQ, question: value };
    if (name === 'answer') this.newQ = { ...this.newQ, answer: value };
  }

  private onNewSelectionChange(e: any) {
    const field = e.detail.additionalCustomEventIdentifier as string;
    const value = e.detail.newValue;
    if (field === 'category') this.newQ = { ...this.newQ, category_id: value };
    if (field === 'difficulty') this.newQ = { ...this.newQ, difficulty_id: Number(value) };
    if (field === 'question_type') this.newQ = { ...this.newQ, question_type: value };
  }

  private onFilterChange(e: any) {
    const field = e.detail.additionalCustomEventIdentifier as string;
    const value = e.detail.newValue || '';
    if (field === 'filter-category') this.filterCategoryId = value;
    if (field === 'filter-difficulty') this.filterDifficultyId = value ? Number(value) : '';
  }

  private get filteredQuestions(): QuestionRow[] {
    let filtered = this.questions;

    if (this.filterCategoryId) {
      filtered = filtered.filter(q => q.category?.id === this.filterCategoryId);
    }

    if (this.filterDifficultyId !== '') {
      filtered = filtered.filter(q => q.difficulty === this.filterDifficultyId);
    }

    return filtered;
  }

  private get canCreate() {
    const q = this.newQ;
    return (
      !!(q.question && q.answer && q.category_id && q.difficulty_id && q.question_type)
    );
  }

  private async createNew() {
    this.creating = true;
    this.createError = null;
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const csrf = getCSRFToken();
      if (csrf) headers['X-CSRFToken'] = csrf;
      const res = await fetch(`${API_BASE}/api/jeopardy/questions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.newQ),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Create failed (${res.status})`);
      const created = await res.json();
      // Insert at top
      this.questions = [{ ...created, dirty: false, saving: false, error: null }, ...this.questions];
      // Reset form
      this.newQ = { question: '', answer: '', category_id: '', difficulty_id: '', question_type: '' };
    } catch (e: any) {
      this.createError = e?.message ?? 'Create failed';
    } finally {
      this.creating = false;
    }
  }

  render() {
    if (this.loading) return html`<div>Loading...</div>`;
    if (this.error) return html`<div class="error">${this.error}</div>`;

    const categoryFilterOptions = [
      { value: '', label: 'All Categories' },
      ...this.categories.map(c => ({ value: c.id, label: c.name }))
    ];

    const difficultyFilterOptions = [
      { value: '', label: 'All Difficulties' },
      ...this.difficulties.map(d => ({ value: String(d), label: String(d) }))
    ];

    const hasActiveFilters = !!(this.filterCategoryId || this.filterDifficultyId);
    const filteredCount = this.filteredQuestions.length;
    const totalCount = this.questions.length;

    return html`
      <div class="toolbar" style="justify-content: space-between; align-items: center;">
        <div style="display:flex; gap:1rem; align-items:center;">
          <h2>All Questions</h2>
          <span class="badge">${hasActiveFilters ? `${filteredCount} / ${totalCount}` : totalCount}</span>
        </div>
        <basic-button @click=${() => navigate('/')}>Back to Main Menu</basic-button>
      </div>

      <!-- Filter Bar -->
      <div class="toolbar" style="background: #f7f7f7; padding: 0.75rem; border-radius: 6px; margin-bottom: 1rem;">
        <div style="display:flex; gap:0.5rem; align-items:center; flex:1;">
          <strong style="min-width: 80px;">Filter by:</strong>
          <basic-select
            .value=${this.filterCategoryId}
            .options=${categoryFilterOptions}
            .additionalCustomEventIdentifier=${'filter-category'}
            @selection-change=${(e: any) => this.onFilterChange(e)}
            style="min-width: 150px;"
          ></basic-select>

          <basic-select
            .value=${this.filterDifficultyId !== '' ? String(this.filterDifficultyId) : ''}
            .options=${difficultyFilterOptions}
            .additionalCustomEventIdentifier=${'filter-difficulty'}
            @selection-change=${(e: any) => this.onFilterChange(e)}
            style="min-width: 150px;"
          ></basic-select>

          ${hasActiveFilters ? html`
            <basic-button @click=${() => { this.filterCategoryId = ''; this.filterDifficultyId = ''; }}>
              Clear Filters
            </basic-button>
          ` : nothing}
        </div>
      </div>

      <!-- Create New Question Bar -->
      <div class="toolbar" style="margin-bottom: 1rem;">
        <basic-input
          .value=${this.newQ.question}
          .label=${''}
          .name=${'question'}
          .id=${'new-question'}
          .inputEventIdentifier=${'new'}
          @input-change=${(e: any) => this.onNewInputChange(e)}
          style="flex:2; min-width: 12rem;"
        ></basic-input>

        <basic-input
          .value=${this.newQ.answer}
          .label=${''}
          .name=${'answer'}
          .id=${'new-answer'}
          .inputEventIdentifier=${'new'}
          @input-change=${(e: any) => this.onNewInputChange(e)}
          style="flex:1; min-width: 10rem;"
        ></basic-input>

        <basic-select
          .value=${this.newQ.category_id ?? ''}
          .options=${this.categories.map(c => ({ value: c.id, label: c.name }))}
          .additionalCustomEventIdentifier=${'category'}
          @selection-change=${(e: any) => this.onNewSelectionChange(e)}
        ></basic-select>

        <basic-select
          .value=${this.newQ.difficulty_id !== '' ? String(this.newQ.difficulty_id) : ''}
          .options=${this.difficulties.map(d => ({ value: String(d), label: String(d) }))}
          .additionalCustomEventIdentifier=${'difficulty'}
          @selection-change=${(e: any) => this.onNewSelectionChange(e)}
        ></basic-select>

        <basic-select
          .value=${this.newQ.question_type ?? ''}
          .options=${Object.values(QuestionType).map(t => ({ value: t, label: QuestionTypeGerman[t] }))}
          .additionalCustomEventIdentifier=${'question_type'}
          @selection-change=${(e: any) => this.onNewSelectionChange(e)}
        ></basic-select>

        <basic-button @click=${this.createNew} ?disabled=${!this.canCreate || this.creating}>${this.creating ? 'Creating…' : 'Create'}</basic-button>
      </div>
      ${this.createError ? html`<div class="error" style="margin-bottom:0.5rem;">${this.createError}</div>` : nothing}

      <table>
        <thead>
          <tr>
            <th style="width: 35%">Question</th>
            <th style="width: 20%">Answer</th>
            <th style="width: 17%">Category</th>
            <th style="width: 13%">Difficulty</th>
            <th style="width: 10%">Type</th>
            <th style="width: 5%">Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.filteredQuestions.map((q) => {
      // Find the original index in the questions array
      const originalIndex = this.questions.findIndex(original => original.id === q.id);
      return this.renderRow(q, originalIndex);
    })}
        </tbody>
      </table>
    `;
  }
}
